import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import AdminPanelCN from 'components/AdminPanelCN/AdminPanelCN'
import HeaderCN from 'components/HeaderCN/HeaderCN'
// Loads from /api/projects/admin, which (unlike /myProjects) still returns
// archived projects — this screen buckets them into its "archived" category.
import { deleteProject, initializeAdminProjects, updateProject } from 'slices/projectSlice'
import { ChevronUp, ChevronDown, ArrowUpDown, Trash2, MapPin } from 'lucide-react'
import MapLocationPopupCN from 'components/popups/MapLocationPopupCN'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from 'components/ui/table'
import { TablePagination } from 'components/ui/pagination'
import { Sheet } from 'components/ui/sheet'
import { Button } from 'components/ui/button'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import ProjectModifyPopup from 'components/popups/ProjectModifyPopup'
import AltRetailerLogo from 'images/vr-new-retail_3.png'

const formatShortDate = (dateStr: string | undefined) => {
	if (!dateStr) return ''
	const d = new Date(dateStr)
	if (isNaN(d.getTime())) return ''
	const mm = String(d.getMonth() + 1).padStart(2, '0')
	const dd = String(d.getDate()).padStart(2, '0')
	const yy = String(d.getFullYear()).slice(-2)
	return `${mm}/${dd}/${yy}`
}

const formatActivityDate = (dateStr: string) => {
	const d = new Date(dateStr)
	const now = new Date()
	const diffMs = now.getTime() - d.getTime()
	const diffMins = Math.floor(diffMs / 60000)
	const diffHrs = Math.floor(diffMs / 3600000)
	const diffDays = Math.floor(diffMs / 86400000)

	if (diffMins < 1) return 'Just now'
	if (diffMins < 60) return `${diffMins}m ago`
	if (diffHrs < 24) return `${diffHrs}h ago`
	if (diffDays < 7) return `${diffDays}d ago`
	return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const headerCells = [
	{ id: 'image', label: '', sortable: false, width: 'w-14' },
	{ id: 'projectName', label: 'Project Name', width: '' },
	{ id: 'assetName', label: 'Location', width: '' },
	{ id: 'address', label: 'Address', width: '' },
	{ id: 'city', label: 'City', width: '' },
	{ id: 'stateProvince', label: 'State', width: 'w-16' },
	{ id: 'postalCode', label: 'Postal Code', width: 'w-24' },
	{ id: 'isActive', label: 'Status', width: 'w-20' },
]

interface ProjectRow {
	_id: string
	projectName: string
	assetName: string
	projectType: string
	address: string
	city: string
	stateProvince: string
	postalCode: string
	image: string
	owner: string
	ownerImage: string
	isActive: boolean
	latitude: number
	longitude: number
}

const ProjectsListCN = () => {
	const dispatch = useDispatch()
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

	const { userInfo } = useSelector((state: any) => state.userLogin)
	const userId = userInfo?._id

	const searchInputRef = useRef<HTMLInputElement>(null)
	const [searchInputValue, setSearchInputValue] = useState('')
	const [openSheet, setOpenSheet] = useState(false)
	const [recordForEdit, setRecordForEdit] = useState<any>(null)
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

	const [orderBy, setOrderBy] = useState<string | null>(null)
	const [order, setOrder] = useState<'asc' | 'desc'>('asc')
	const [page, setPage] = useState(0)
	const [rowsPerPage, setRowsPerPage] = useState(10)

	// Map popup state
	const [openMap, setOpenMap] = useState(false)
	const [mapLocation, setMapLocation] = useState<any>(null)

	const handleOpenMap = (e: React.MouseEvent, loc: any) => {
		e.stopPropagation()
		setMapLocation(loc)
		setOpenMap(true)
	}

	const {
		entities: projectEntities,
		ids: projectIds,
		isLoading: loading,
		isInitialized,
		error,
	} = useSelector((state: any) => state.projects)

	const records: ProjectRow[] = useMemo(() => {
		const projects = projectIds.map((id: string) => projectEntities[id])
		if (!projects || projects.length === 0) return []
		return projects.map((item: any) => ({
			_id: item._id,
			projectName: item.projectName,
			assetName: item.projectAsset?.assetName || '',
			projectType: item.projectType,
			address: item.projectAsset?.address || '',
			city: item.projectAsset?.city || '',
			stateProvince: item.projectAsset?.stateProvince || '',
			postalCode: item.projectAsset?.postalCode || '',
			image: item.projectAsset?.image || '',
			owner: item.owner?._id || '',
			ownerImage: item.owner?.image || '',
			isActive: item.isActive,
			latitude: item.projectAsset?.latitude,
			longitude: item.projectAsset?.longitude,
		}))
	}, [projectIds, projectEntities])

	useEffect(() => {
		// @ts-ignore
		dispatch(initializeAdminProjects())
	}, [dispatch, isInitialized])

	const handleSort = (columnId: string) => {
		if (orderBy === columnId) {
			setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setOrderBy(columnId)
			setOrder('asc')
		}
	}

	const filteredRecords = useMemo(() => {
		if (!records || records.length === 0) return []
		if (!searchInputValue) return records
		const lowerValue = searchInputValue.toLowerCase()
		return records.filter(
			x =>
				x.projectName.toLowerCase().includes(lowerValue) ||
				x.assetName.toLowerCase().includes(lowerValue) ||
				x.address.toLowerCase().includes(lowerValue) ||
				x.city.toLowerCase().includes(lowerValue) ||
				x.stateProvince.toLowerCase().includes(lowerValue) ||
				x.postalCode.toLowerCase().includes(lowerValue),
		)
	}, [records, searchInputValue])

	const sortedRecords = useMemo(() => {
		if (!orderBy) return filteredRecords
		return [...filteredRecords].sort((a: any, b: any) => {
			let aVal: string
			let bVal: string
			if (orderBy === 'isActive') {
				aVal = a.isActive ? 'active' : 'inactive'
				bVal = b.isActive ? 'active' : 'inactive'
			} else {
				aVal = (a[orderBy] || '').toString().toLowerCase()
				bVal = (b[orderBy] || '').toString().toLowerCase()
			}
			if (aVal < bVal) return order === 'asc' ? -1 : 1
			if (aVal > bVal) return order === 'asc' ? 1 : -1
			return 0
		})
	}, [filteredRecords, orderBy, order])

	const paginatedRecords = useMemo(() => {
		const start = page * rowsPerPage
		return sortedRecords.slice(start, start + rowsPerPage)
	}, [sortedRecords, page, rowsPerPage])

	const SortIcon = ({ columnId }: { columnId: string }) => {
		if (orderBy !== columnId) return <ArrowUpDown className="ml-1 h-3 w-3 text-gray-300" />
		return order === 'asc' ? (
			<ChevronUp className="ml-1 h-3 w-3 text-[#0080fe]" />
		) : (
			<ChevronDown className="ml-1 h-3 w-3 text-[#0080fe]" />
		)
	}

	const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchInputValue(e.target.value)
		setPage(0)
	}

	const handleRowClick = (projectRow: ProjectRow) => {
		// Get the raw entity to access updateHistory
		const rawProject = projectEntities[projectRow._id]
		setRecordForEdit({
			...projectRow,
			...rawProject,
			// Keep the flat fields for the edit form
			assetName: projectRow.assetName,
			address: projectRow.address,
			city: projectRow.city,
			stateProvince: projectRow.stateProvince,
			postalCode: projectRow.postalCode,
			image: projectRow.image,
		})
		setShowDeleteConfirm(false)
		setOpenSheet(true)
	}

	const handleEditProject = async (values: any) => {
		const updatedProject = { ...values, updateNote: 'Edited Project.' }
		// @ts-ignore
		await dispatch(updateProject(updatedProject))
		// @ts-ignore
		await dispatch(initializeAdminProjects())
		setOpenSheet(false)
	}

	const handleDeleteProject = async () => {
		if (!recordForEdit) return
		// @ts-ignore
		await dispatch(deleteProject({ projectId: recordForEdit._id }))
		// @ts-ignore
		await dispatch(initializeAdminProjects())
		setOpenSheet(false)
		setShowDeleteConfirm(false)
	}

	const renderContent = () => {
		if (loading) {
			return (
				<div className="flex items-center justify-center py-20">
					<div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-[#0080fe]" />
				</div>
			)
		}

		if (error && (!records || records.length === 0)) {
			return <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-600">{error}</div>
		}

		if (!records || records.length === 0) {
			return (
				<div className="flex flex-col items-center justify-center py-20 text-gray-400">
					<svg className="h-16 w-16 mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
					</svg>
					<p className="text-sm">No projects found.</p>
				</div>
			)
		}

		return (
			<>
			<div className="max-w-[1080px] mx-auto w-full mt-4 bg-white rounded-lg border border-gray-200 p-5">
				{/* Search bar */}
				<div className="mb-4">
					<input
						ref={searchInputRef}
						type="text"
						placeholder="Search by project, location, address..."
						value={searchInputValue}
						onChange={handleSearch}
						className="w-full max-w-sm rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 placeholder-gray-400 focus:border-[#0080fe] focus:outline-none focus:ring-1 focus:ring-[#0080fe]"
					/>
				</div>

				{/* Table */}
				<div className="rounded-lg border border-gray-200 overflow-x-clip [&>div]:overflow-x-clip">
					<Table className="table-fixed w-full">
						<TableHeader>
							<TableRow className="border-b-2 border-[#0080fe] hover:bg-transparent">
								{headerCells.map(cell => (
									<TableHead
										key={cell.id}
										className={[
											cell.sortable !== false ? 'cursor-pointer select-none' : '',
											'text-gray-700 text-xs font-semibold whitespace-nowrap',
											cell.width || '',
										].join(' ')}
										onClick={cell.sortable !== false ? () => handleSort(cell.id) : undefined}
									>
										<span className="inline-flex items-center">
											{cell.label}
											{cell.sortable !== false && cell.label && <SortIcon columnId={cell.id} />}
										</span>
									</TableHead>
								))}
							</TableRow>
						</TableHeader>
						<TableBody>
							{paginatedRecords.map((project: ProjectRow) => (
								<TableRow
									key={project._id}
									className="h-7 hover:bg-[#fffbf2] cursor-pointer"
									onClick={() => handleRowClick(project)}
								>
									<TableCell className="py-1 text-sm">
										<Avatar className="h-8 w-8">
											<AvatarImage src={project.image || AltRetailerLogo} alt={project.assetName} />
											<AvatarFallback className="bg-gray-200 text-xs text-gray-500">
												{project.projectName?.charAt(0) || 'P'}
											</AvatarFallback>
										</Avatar>
									</TableCell>
									<TableCell className="py-1 text-sm truncate">{project.projectName}</TableCell>
									<TableCell className="py-1 text-sm truncate">{project.assetName}</TableCell>
									<TableCell className="py-1 text-sm truncate">
										<button
											type="button"
											onClick={e => handleOpenMap(e, project)}
											className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs text-gray-600 hover:bg-[#0080fe]/10 hover:text-[#0080fe] hover:border-[#0080fe]/20 transition-colors cursor-pointer"
										>
											<MapPin className="h-3 w-3" />
											{project.address}
										</button>
									</TableCell>
									<TableCell className="py-1 text-sm truncate">{project.city}</TableCell>
									<TableCell className="py-1 text-sm">{project.stateProvince}</TableCell>
									<TableCell className="py-1 text-sm">{project.postalCode}</TableCell>
									<TableCell className="py-1 text-sm">
										<span className={project.isActive ? 'text-green-600' : 'text-gray-400'}>
											{project.isActive ? 'Active' : 'Inactive'}
										</span>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>

				{sortedRecords.length > 4 && (
					<TablePagination
						page={page}
						rowsPerPage={rowsPerPage}
						count={sortedRecords.length}
						onPageChange={setPage}
						onRowsPerPageChange={setRowsPerPage}
					/>
				)}
			</div>

			{/* Edit Sheet */}
			<Sheet open={openSheet} onClose={() => setOpenSheet(false)} side="right" className="!w-[550px]">
				{recordForEdit ? (
					<>
						{/* Header */}
						<div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
							<div className="min-w-0 flex-1">
								<h2 className="text-lg font-semibold text-gray-900 truncate">{recordForEdit.projectName}</h2>
								{recordForEdit.assetName && (
									<p className="text-sm text-gray-500 truncate">
										{recordForEdit.assetName}{recordForEdit.city ? `, ${recordForEdit.city}` : ''}{recordForEdit.stateProvince ? `, ${recordForEdit.stateProvince}` : ''}
									</p>
								)}
								{(recordForEdit.createdAt || recordForEdit.updatedAt) && (
									<div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
										{recordForEdit.createdAt && <span>Created: {formatShortDate(recordForEdit.createdAt)}</span>}
										{recordForEdit.updatedAt && <span>Updated: {formatShortDate(recordForEdit.updatedAt)}</span>}
									</div>
								)}
							</div>
							<button
								type="button"
								onClick={() => setOpenSheet(false)}
								className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors ml-3 flex-shrink-0"
								title="Close (Esc)"
							>
								<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						</div>

						{/* Body */}
						<div className="flex-1 overflow-y-auto p-5">
							{/* Edit Form */}
							<ProjectModifyPopup editProject={handleEditProject} recordForEdit={recordForEdit} />

							{/* Activity History */}
							{recordForEdit.updateHistory && recordForEdit.updateHistory.length > 0 && (
								<div className="mt-6 pt-5 border-t border-gray-200">
									<div className="flex items-center gap-2 mb-4">
										<svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
										</svg>
										<h3 className="text-sm font-semibold text-gray-900">Activity</h3>
										<span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
											{recordForEdit.updateHistory.length}
										</span>
									</div>
									<div className="flow-root">
										<ul className="-mb-6">
											{recordForEdit.updateHistory
												.slice()
												.reverse()
												.map((entry: any, idx: number, arr: any[]) => {
													const isLast = idx === arr.length - 1
													return (
														<li key={idx} className="relative pb-6 last:pb-0">
															{/* Timeline connector */}
															{!isLast && (
																<span
																	className="absolute left-[15px] top-8 -ml-px h-full w-0.5 bg-gray-200"
																	aria-hidden="true"
																/>
															)}
															<div className="relative flex items-start gap-3">
																{/* User avatar */}
																<Avatar className="h-8 w-8 flex-shrink-0">
																	{entry.updatedBy?.image ? (
																		<>
																			<AvatarImage src={entry.updatedBy.image} alt={entry.updatedBy.name || ''} />
																			<AvatarFallback className="text-xs bg-gray-200 text-gray-600">
																				{(entry.updatedBy.name || '?').charAt(0).toUpperCase()}
																			</AvatarFallback>
																		</>
																	) : (
																		<AvatarFallback className="bg-amber-50 text-amber-600 text-xs">
																			{(entry.updatedBy?.name || '?').charAt(0).toUpperCase()}
																		</AvatarFallback>
																	)}
																</Avatar>
																{/* Content */}
																<div className="min-w-0 flex-1 pt-0.5">
																	<span className="text-sm font-medium text-amber-600">Updated</span>
																	{entry.updateNote && (
																		<p className="mt-0.5 text-sm text-gray-600">{entry.updateNote}</p>
																	)}
																	<p className="mt-1 text-xs text-gray-400">
																		{entry.updatedAt && formatActivityDate(entry.updatedAt)}
																		{entry.updatedBy?.name && ` · ${entry.updatedBy.name}`}
																	</p>
																</div>
															</div>
														</li>
													)
												})}
										</ul>
									</div>
								</div>
							)}
						</div>

						{/* Footer — Delete */}
						<div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex items-center">
							{showDeleteConfirm ? (
								<div className="flex items-center gap-2">
									<span className="text-sm text-red-600">Delete this project?</span>
									<Button variant="danger" onClick={handleDeleteProject}>
										Yes, Delete
									</Button>
									<Button variant="ghost" onClick={() => setShowDeleteConfirm(false)}>
										Cancel
									</Button>
								</div>
							) : (
								<Button variant="ghost-red" onClick={() => setShowDeleteConfirm(true)}>
									<Trash2 className="w-4 h-4 mr-1" />
									Delete
								</Button>
							)}
						</div>
					</>
				) : (
					<div />
				)}
			</Sheet>

			{openMap && mapLocation && (
				<MapLocationPopupCN
					open={openMap}
					onClose={() => setOpenMap(false)}
					location={mapLocation}
				/>
			)}
			</>
		)
	}

	return (
		<div className="flex h-screen overflow-hidden bg-gray-50">
			<AdminPanelCN mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />

			<div className="flex-1 flex flex-col overflow-hidden">
				<HeaderCN onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

				<div className="flex-1 flex overflow-hidden">
					<main className="flex-1 overflow-y-auto p-6">
						<h2 className="text-2xl font-semibold text-gray-800 mb-4">Projects</h2>
						{renderContent()}
					</main>
				</div>
			</div>
		</div>
	)
}

export default ProjectsListCN
